import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarDays, CalendarClock, Info, Lock, MapPin, Save, Search, Send, WifiOff } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import { clearDraft, draftKey, loadDraft, saveDraft } from "@/lib/wmDraft";
import {
    SC_TIMEOUT_MS, STATUS_TONE, fmt, fmtDateTime, fmtDay, fmtMoney, fmtPeriod, fmtQuantity,
    labelOf, scLineErrors, scMessage,
} from "./scUtils";
import {
    CountLine, entryFits, entryTotal, sameEntry, sameText, savedLine, serverEntry, toPayload,
} from "./CountLine";
import { SubmitDialog } from "./SubmitDialog";

const C = OPS.sc;
const S = OPS.sc.sheet;

// edit_reason (sc_core.edit_state) -> the one sentence shown for it.
const REASON_KEYS = {
    open: "stk_reason_open", reopened: "stk_reason_reopened", finance: "stk_reason_finance",
    read_only: "stk_reason_read_only", inactive: "stk_reason_inactive", future: "stk_reason_future",
    approved: "stk_reason_approved", submitted: "stk_reason_submitted", not_yet: "stk_reason_not_yet",
    closed: "stk_reason_closed",
};

/** Search folding: case, alef / yaa / taa-marbuta forms and tatweel. */
const fold = (s) => String(s || "").toLowerCase()
    .replace(/[أإآ]/g, "ا").replace(/ى/g, "ي").replace(/ة/g, "ه").replace(/ـ/g, "");

const round2 = (n) => Math.round(n * 100) / 100;

/** The draft holds ONLY unsaved edits; an empty one is removed. */
function persistDraft(key, edits) {
    if (Object.keys(edits).length) saveDraft(key, { edits });
    else clearDraft(key);
}

const chipClass = (on) =>
    `h-9 shrink-0 rounded-full border px-3 text-xs font-semibold transition-[background-color,color,border-color] duration-150 ${
        on
            ? "border-[color:var(--brand-orange)] bg-[color:var(--brand-orange)] text-white shadow-[var(--shadow-sm)]"
            : "border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] text-[color:var(--ink)] hover:bg-[color:var(--brand-orange-soft)]"
    }`;

const lockedClass =
    "flex h-10 items-center gap-2 rounded-[var(--radius-sm)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface-2)] px-3 text-sm font-semibold text-[color:var(--ink)]";

/** Location picker: one location is a LOCKED chip — no choice that does not exist. */
function LocationPicker({ locations, value, onChange, disabled }) {
    const { t, tn } = useI18n();
    const list = locations || [];
    const current = list.find((l) => l.id === value);
    const suffix = (l) => [
        l.kind === "warehouse" ? t("stk_kind_warehouse") : null,
        l.is_active === false ? t("stk_inactive") : null,
    ].filter(Boolean).map((s) => ` — ${s}`).join("");

    if (list.length <= 1) {
        return (
            <div data-testid={C.locationSelect} data-locked="true" className={lockedClass}>
                <MapPin className="h-4 w-4 shrink-0 text-[color:var(--brand-brown)]" />
                <span className="truncate">{current ? tn(current) : t("stk_select_location")}</span>
                {current?.is_active === false ? (
                    <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">{t("stk_inactive")}</Badge>
                ) : null}
            </div>
        );
    }
    return (
        <Select value={value || ""} onValueChange={onChange} disabled={disabled}>
            <SelectTrigger data-testid={C.locationSelect} className="h-10 w-full">
                <MapPin className="h-4 w-4 shrink-0 text-[color:var(--brand-brown)]" />
                <SelectValue placeholder={t("stk_select_location")} />
            </SelectTrigger>
            <SelectContent>
                {list.map((l) => (
                    <SelectItem key={l.id} value={l.id} data-testid={C.locationOption(l.id)}>
                        {tn(l)}{suffix(l)}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

/** Period: finance picks from `scope.periods`; a counter sees the one period as a locked chip. */
function PeriodPicker({ periods, value, onChange, canPick, disabled }) {
    const { lang } = useI18n();
    if (!canPick) {
        return (
            <div data-testid={C.periodSelect} data-locked="true" data-period={value} className={lockedClass}>
                <CalendarDays className="h-4 w-4 shrink-0 text-[color:var(--brand-brown)]" />
                <span className="truncate">{fmtPeriod(value, lang)}</span>
            </div>
        );
    }
    return (
        <Select value={value || ""} onValueChange={onChange} disabled={disabled}>
            <SelectTrigger data-testid={C.periodSelect} data-period={value} className="h-10 w-full">
                <CalendarDays className="h-4 w-4 shrink-0 text-[color:var(--brand-brown)]" />
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                {(periods || []).map((p) => (
                    <SelectItem key={p} value={p}>{fmtPeriod(p, lang)}</SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

/** «جرد شهر أكتوبر ٢٠٢٦ · يغلق ٢ نوفمبر» while a window is open, else when the next one opens. */
function WindowBanner({ win }) {
    const { t, lang } = useI18n();
    if (!win?.period) return null;
    const open = !!win.is_open;
    const text = open
        ? fmt(t("stk_window_open"), { period: fmtPeriod(win.period, lang), end: fmtDay(win.end, lang) })
        : fmt(t("stk_window_next"), { period: fmtPeriod(win.period, lang), start: fmtDay(win.start, lang) });
    return (
        <div data-testid={C.windowBanner} data-open={open ? "true" : "false"} data-period={win.period}
             className={`flex items-center gap-2 rounded-[var(--radius-lg)] px-3 py-2.5 text-xs font-semibold ${
                 open
                     ? "bg-[color:var(--success-soft)] text-[color:var(--success-soft-fg)]"
                     : "bg-[color:var(--bg-surface-2)] text-[color:var(--ink-muted)]"
             }`}>
            <CalendarClock className="h-4 w-4 shrink-0" />
            <span>{text}</span>
        </div>
    );
}

/** Months finance reopened for this counter (scope.reopened), other than the one on screen. */
function ReopenedBanner({ reopened, locations, locationId, period, onOpen }) {
    const { t, tn, lang } = useI18n();
    const rows = (reopened || []).filter((r) => !(r.location_id === locationId && r.period === period));
    if (!rows.length) return null;
    return (
        <div data-testid={C.reopened}
             className="space-y-1.5 rounded-[var(--radius-lg)] bg-[color:var(--warning-soft)] px-3 py-2.5">
            {rows.map((r) => {
                const loc = locations.find((l) => l.id === r.location_id);
                return (
                    <div key={`${r.location_id}|${r.period}`}
                         className="flex items-center gap-2 text-xs font-semibold text-[color:var(--warning-soft-fg)]">
                        <CalendarClock className="h-4 w-4 shrink-0" />
                        <span className="min-w-0 flex-1">
                            {fmt(t("stk_reopened_notice"), {
                                period: fmtPeriod(r.period, lang), location: loc ? tn(loc) : "",
                                until: fmtDay(r.open_until, lang),
                            })}
                        </span>
                        <Button size="sm" variant="outline" data-testid={C.reopenedOpen(r.location_id, r.period)}
                                onClick={() => onOpen(r)}>
                            {t("stk_reopened_open")}
                        </Button>
                    </div>
                );
            })}
        </div>
    );
}

/** No response at all (timeout, dropped connection): the inline offline / retry box. */
function OfflineBox({ onRetry, busy, text }) {
    const { t } = useI18n();
    return (
        <div data-testid={C.offlineError}
             className="mt-3 flex items-center justify-between gap-2 rounded-[var(--radius-sm)] bg-[color:rgba(220,38,38,0.08)] px-3 py-2">
            <span className="flex items-center gap-1.5 text-xs font-semibold text-[color:var(--danger)]">
                <WifiOff className="h-3.5 w-3.5 shrink-0" />
                {text || t("stk_offline_error")}
            </span>
            <Button size="sm" variant="outline" data-testid={C.retry} onClick={onRetry} disabled={busy}>
                {t("stk_retry")}
            </Button>
        </div>
    );
}

/**
 * «الجرد» tab: one location + one month. Lines load from `GET /sc/sheet`; the
 * user's UNSAVED edits live in a localStorage draft (wmDraft, kind "sc") until
 * «حفظ» sends only the changed lines (`PUT /sc/sheet/lines`) — no autosave.
 * «إرسال الجرد» saves pending edits first, then opens the submit dialog.
 * `?loc=&period=` preselect (the board's «تعديل»); a counter gets the scope's
 * default period, or a month finance reopened for that location (scope.reopened). A counted 0 is a count; only blank is "not counted".
 */
export function CountSheet({ scope }) {
    const { t, tn, lang } = useI18n();
    const { me } = useAuth();
    const userId = me?.user?.id;
    const [searchParams, setSearchParams] = useSearchParams();

    const locations = useMemo(() => scope?.locations || [], [scope]);
    const periods = useMemo(() => scope?.periods || [], [scope]);
    const canPickPeriod = !!scope?.caps?.can_finance;

    // The pickers live in the URL: a refresh or the board's «تعديل» link lands
    // on the same sheet; a value outside the scope falls back to the default.
    const paramLoc = searchParams.get("loc");
    const paramPeriod = searchParams.get("period");
    // A counter who may SEE more than they count (an ops manager with sc_count) opens on
    // one of their own locations: `default_location_id` is just the first active one in
    // scope, where such a counter is read-only (sc_core.edit_state).
    const homeIds = me?.user?.sc_location_ids || [];
    const homeLoc = canPickPeriod ? null : locations.find((l) => l.is_active !== false && homeIds.includes(l.id));
    const fallbackLoc = homeLoc?.id || scope?.default_location_id
        || (locations.find((l) => l.is_active !== false) || locations[0])?.id || "";
    const locationId = locations.some((l) => l.id === paramLoc) ? paramLoc : fallbackLoc;
    // A counter stays on the default month, except a month finance reopened for this location.
    const reopenedHere = (scope?.reopened || []).filter((r) => r.location_id === locationId).map((r) => r.period);
    const period = (canPickPeriod && periods.includes(paramPeriod)) || reopenedHere.includes(paramPeriod)
        ? paramPeriod : (scope?.default_period || "");
    // ...and can always switch back: the default month plus the reopened ones, newest first.
    const counterPeriods = [...new Set([scope?.default_period, ...reopenedHere].filter(Boolean))].sort().reverse();
    const location = locations.find((l) => l.id === locationId);

    const setParam = (key, value) => {
        const next = new URLSearchParams(searchParams);
        next.set(key, value);
        setSearchParams(next, { replace: true });
    };
    const openReopened = (r) => {
        const next = new URLSearchParams(searchParams);
        next.set("loc", r.location_id);
        next.set("period", r.period);
        setSearchParams(next, { replace: true });
    };

    // ---------------------------------------------------------------- the sheet
    const sheetKey = `${locationId}|${period}`;
    const [sheet, setSheet] = useState(null);        // {key, data}: data is shown only for its own key
    const [loading, setLoading] = useState(false);
    const [loadErr, setLoadErr] = useState(null);
    const seq = useRef(0);

    const load = useCallback(async ({ quiet } = {}) => {
        if (!locationId || !period) return null;
        const my = ++seq.current;
        if (!quiet) setLoading(true);
        setLoadErr(null);
        try {
            const r = await api.get("/sc/sheet", { params: { location_id: locationId, period } });
            if (my !== seq.current) return null;
            setSheet({ key: `${locationId}|${period}`, data: r.data });
            return r.data;
        } catch (e) {
            if (my === seq.current) setLoadErr(e);
            return null;
        } finally {
            if (my === seq.current) setLoading(false);
        }
    }, [locationId, period]);

    useEffect(() => { load(); }, [load]);

    const data = sheet && sheet.key === sheetKey ? sheet.data : null;
    const items = useMemo(() => data?.items || [], [data]);
    const lines = useMemo(() => data?.lines || {}, [data]);
    const values = data?.values || null;
    const editable = !!data?.editable;
    const status = data?.count?.status || "not_started";
    const itemsById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
    // stable per-line objects, so the memoised lines re-render only when they change
    const serverEntries = useMemo(() => {
        const m = {};
        for (const it of items) m[it.id] = serverEntry(it, lines[it.id]);
        return m;
    }, [items, lines]);
    const savedValues = useMemo(() => {
        const m = {};
        for (const [id, v] of Object.entries(values || {})) m[id] = { value: v?.value ?? null, unitCost: v?.unit_cost ?? null };
        return m;
    }, [values]);

    // ------------------------------------------------------------ draft edits
    const dkey = draftKey({ userId, branchId: locationId, date: period, type: "count", kind: "sc" });
    const [edits, setEdits] = useState({});
    const [errors, setErrors] = useState({});
    const [busy, setBusy] = useState(false);
    const [saveFailed, setSaveFailed] = useState(false);
    const [submitOpen, setSubmitOpen] = useState(false);
    const dkeyRef = useRef(dkey);
    const linesRef = useRef(lines);
    const itemsRef = useRef(itemsById);
    const listRef = useRef(null);
    dkeyRef.current = dkey;
    linesRef.current = lines;
    itemsRef.current = itemsById;

    // `edits` holds what the inputs show; the DRAFT holds only the entries that
    // differ from the stored line ("5." over a stored 5 is on screen, not unsaved).
    const unsavedOf = useCallback((all) => {
        const out = {};
        for (const [id, e] of Object.entries(all)) {
            const it = itemsRef.current.get(id);
            if (!it || !sameEntry(it, e, serverEntry(it, linesRef.current[id]))) out[id] = e;
        }
        return out;
    }, []);

    useEffect(() => {
        const d = loadDraft(dkey);
        const restored = d?.edits && typeof d.edits === "object" ? d.edits : {};
        setEdits(restored);
        setErrors({});
        setSaveFailed(false);
        if (Object.keys(restored).length) toast(t("stk_draft_restored"), { id: "sc-draft-restored" });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dkey]);

    // A fresh sheet drops draft entries it already holds (saved elsewhere, or
    // typed back to the stored text), entries for items no longer listed, and
    // entries whose shape no longer fits the item (pack helper added / removed).
    // An entry spelt differently from the stored value ("5." over 5) stays: the
    // user may be typing in it while the sheet reloads.
    useEffect(() => {
        if (!data) return;
        setEdits((prev) => {
            const ids = Object.keys(prev);
            if (!ids.length) return prev;
            const byId = new Map((data.items || []).map((i) => [i.id, i]));
            const next = {};
            for (const id of ids) {
                const it = byId.get(id);
                if (it && entryFits(it, prev[id])
                    && !sameText(it, prev[id], serverEntry(it, (data.lines || {})[id]))) next[id] = prev[id];
            }
            if (Object.keys(next).length === ids.length) return prev;
            persistDraft(dkeyRef.current, unsavedOf(next));
            return next;
        });
    }, [data, unsavedOf]);

    // Stable for the memoised lines: the stored lines and the key come from refs.
    const setEntry = useCallback((item, entry) => {
        setSaveFailed(false);
        setErrors((e) => {
            if (!e[item.id]) return e;
            const n = { ...e };
            delete n[item.id];
            return n;
        });
        const server = serverEntry(item, linesRef.current[item.id]);
        setEdits((prev) => {
            const next = { ...prev };
            // Typing the stored text back is not an edit. Compare the TEXT, not the
            // quantity: dropping "5." (== 5) would snap the input back to "5" and
            // the decimal point could never be typed after a stored value.
            if (sameText(item, entry, server)) delete next[item.id];
            else next[item.id] = entry;
            persistDraft(dkeyRef.current, unsavedOf(next));
            return next;
        });
    }, [unsavedOf]);

    const dirtyIds = useMemo(() => Object.keys(edits).filter((id) => {
        const it = itemsById.get(id);
        return it && entryFits(it, edits[id]) && !sameEntry(it, edits[id], serverEntries[id]);
    }), [edits, itemsById, serverEntries]);
    const dirtySet = useMemo(() => new Set(editable ? dirtyIds : []), [dirtyIds, editable]);
    const unsavedLocked = !editable ? dirtyIds.length : 0;

    // the inputs show what was typed (dirty or not); `dirtySet` decides what is unsaved
    const entryOf = (it) => (editable && edits[it.id] && entryFits(it, edits[it.id]) ? edits[it.id] : serverEntries[it.id]);
    const countedNow = (it) => (dirtySet.has(it.id) ? entryTotal(it, edits[it.id]) !== null : !!lines[it.id]);

    // ----------------------------------------------------------------- values
    // A saved line shows the server's value; an edited one is re-priced at the
    // same unit cost (when the server has one), so the total follows typing.
    // The server's unit cost is per the unit the line was SAVED in; an edit is
    // typed (and saved) in the item's CURRENT count unit, so a line saved before
    // finance changed that unit cannot be re-priced here: it waits for the save.
    const savedInOtherUnit = (it, line) => !!(it && line?.unit && line.unit !== it.count_unit);
    const lineValue = (it) => {
        if (!values) return null;
        if (!dirtySet.has(it.id)) return savedValues[it.id] || null;
        const q = entryTotal(it, edits[it.id]);
        const unitCost = savedValues[it.id]?.unitCost ?? null;
        if (q === null || !savedValues[it.id] || savedInOtherUnit(it, lines[it.id])) return null;
        return { value: unitCost === null ? null : round2(q * unitCost), unitCost };
    };
    const totalValue = useMemo(() => {
        const base = data?.totals?.value;
        if (!values || base === null || base === undefined) return null;
        let total = Number(base) || 0;
        for (const id of dirtySet) {
            const sv = savedValues[id];
            if (!sv || sv.unitCost === null) continue;   // no known cost: the line adds nothing yet
            const it = itemsById.get(id);
            const ln = lines[id];
            if (it && ln?.unit && ln.unit !== it.count_unit) continue;   // saved in an older unit: priced on save
            const q = entryTotal(it, edits[id]);
            total += (q === null ? 0 : q * sv.unitCost) - (sv.value || 0);
        }
        return round2(total);
    }, [values, data, dirtySet, savedValues, itemsById, edits, lines]);

    // ---------------------------------------------------------------- filters
    const [q, setQ] = useState("");
    const [group, setGroup] = useState("all");
    const [uncountedOnly, setUncountedOnly] = useState(false);
    const groups = useMemo(() => {
        const out = [];
        for (const it of items) if (!out.includes(it.group || "")) out.push(it.group || "");
        return out;
    }, [items]);
    useEffect(() => { if (group !== "all" && !groups.includes(group)) setGroup("all"); }, [groups, group]);

    const shown = useMemo(() => {
        const s = fold(q.trim());
        return items.filter((it) => {
            if (group !== "all" && (it.group || "") !== group) return false;
            // a line being edited stays in view, or it would vanish on the first digit
            if (uncountedOnly && lines[it.id] && !(it.id in edits)) return false;
            if (!s) return true;
            return [it.code, it.erp_code, it.name_ar, it.name_en].some((x) => fold(x).includes(s));
        });
    }, [items, group, uncountedOnly, lines, edits, q]);

    const sections = useMemo(() => {
        const m = new Map();
        for (const it of shown) {
            const g = it.group || "";
            if (!m.has(g)) m.set(g, []);
            m.get(g).push(it);
        }
        return [...m.entries()];
    }, [shown]);

    const countedN = items.reduce((n, it) => n + (countedNow(it) ? 1 : 0), 0);
    const missingSaved = items.reduce((n, it) => n + (lines[it.id] ? 0 : 1), 0);
    const groupProgress = (g) => {
        let done = 0;
        let all = 0;
        for (const it of items) {
            if ((it.group || "") !== g) continue;
            all += 1;
            if (countedNow(it)) done += 1;
        }
        return [done, all];
    };

    // Enter / «التالي» on the phone keyboard moves to the next quantity field.
    const focusNext = useCallback((el) => {
        const all = Array.from(listRef.current?.querySelectorAll("input[data-sc-qty]") || []);
        const i = all.indexOf(el);
        if (i !== -1 && all[i + 1]) all[i + 1].focus();
        else el.blur();
    }, []);

    // ------------------------------------------------------------------- save
    const save = async () => {
        if (!dirtyIds.length) { toast(t("stk_nothing_changed"), { id: "sc-nothing-changed" }); return true; }
        const myKey = dkey;
        const mySheet = sheetKey;
        const snapshot = edits;
        const ids = [...dirtyIds];
        const payloadLines = ids.map((id) => toPayload(itemsById.get(id), snapshot[id]));
        setBusy(true);
        setErrors({});
        setSaveFailed(false);
        try {
            const r = await api.put("/sc/sheet/lines",
                                    { location_id: locationId, period, lines: payloadLines },
                                    { timeout: SC_TIMEOUT_MS });
            // show the save at once (the reload below brings values and totals)
            setSheet((s) => {
                if (!s || s.key !== mySheet) return s;
                const nextLines = { ...(s.data.lines || {}) };
                for (const id of ids) {
                    const ln = savedLine(itemsById.get(id), snapshot[id]);
                    if (ln) nextLines[id] = { ...(nextLines[id] || {}), ...ln };
                    else delete nextLines[id];
                }
                return { ...s, data: { ...s.data, lines: nextLines } };
            });
            // The STORED draft drops what was saved right here: on a slow phone the user may
            // have switched tab meanwhile (this screen unmounted, so the state update below
            // never runs), and a leftover entry would come back as an «unsaved» edit later.
            const d = { ...(loadDraft(myKey)?.edits || {}) };
            for (const id of ids) if (d[id] && sameEntry(itemsById.get(id), d[id], snapshot[id])) delete d[id];
            persistDraft(myKey, d);
            // on screen: drop what was saved, unless it was retyped while the request ran
            if (dkeyRef.current === myKey) {
                setEdits((prev) => {
                    const next = { ...prev };
                    for (const id of ids) if (prev[id] === snapshot[id]) delete next[id];
                    persistDraft(myKey, unsavedOf(next));
                    return next;
                });
            }
            const n = ((r.data?.saved ?? 0) + (r.data?.cleared ?? 0)) || payloadLines.length;
            toast.success(fmt(t("stk_saved_n"), { n: fmtQuantity(n, lang) }));
            await load({ quiet: true });
            return true;
        } catch (e) {
            const le = scLineErrors(e);
            if (Object.keys(le).length) {
                const mapped = {};
                Object.entries(le).forEach(([idx, err]) => {
                    const line = payloadLines[Number(idx)];
                    if (line) mapped[line.item_id] = err;
                });
                setErrors(mapped);
                toast.error(scMessage(e, lang) || t("stk_offline_error"));
                const first = Object.keys(mapped)[0];
                if (first) {
                    requestAnimationFrame(() => {
                        listRef.current?.querySelector(`[data-testid="${S.line(first)}"]`)
                            ?.scrollIntoView({ block: "center", behavior: "smooth" });
                    });
                }
            } else {
                const msg = scMessage(e, lang);
                if (msg) {
                    toast.error(msg);
                    // the sheet may have changed state (window closed, submitted): show it as it is
                    load({ quiet: true });
                } else {
                    setSaveFailed(true);
                }
            }
            return false;
        } finally {
            setBusy(false);
        }
    };

    const startSubmit = async () => {
        if (dirtyIds.length) {
            const ok = await save();
            if (!ok) return;
        }
        setSubmitOpen(true);
    };

    // ------------------------------------------------------------------ render
    const statusLabel = labelOf(scope?.labels?.statuses, status, lang) || status;
    const reason = data?.edit_reason || "";
    const reasonText = REASON_KEYS[reason]
        ? fmt(t(REASON_KEYS[reason]), {
            until: fmtDay(data?.count?.open_until, lang),
            start: fmtDay(data?.window?.start, lang),
            end: fmtDay(data?.window?.end, lang),
        })
        : null;
    const canSubmit = editable && (status === "not_started" || status === "draft");
    const loadMsg = loadErr ? scMessage(loadErr, lang) : null;
    const who = (ref) => (ref && typeof ref === "object" ? ref.name : ref) || "";
    const stamp = (() => {
        const c = data?.count;
        if (!c || status === "not_started") return null;
        if (status === "approved" && c.approved_at) return { key: "stk_stamp_approved", at: c.approved_at, by: who(c.approved_by) };
        if (status === "submitted" && c.submitted_at) return { key: "stk_stamp_submitted", at: c.submitted_at, by: who(c.submitted_by) };
        if (c.updated_at) return { key: "stk_stamp_saved", at: c.updated_at, by: who(c.updated_by) };
        return null;
    })();

    const errorNote = (msg) => (
        <div data-testid={C.note}
             className="flex items-start gap-2 rounded-[var(--radius-lg)] border border-dashed border-[color:var(--border-soft)] bg-[color:var(--bg-surface-2)] px-4 py-3">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--warning-soft-fg)]" />
            <p className="text-sm font-semibold text-[color:var(--ink-muted)]">{msg}</p>
        </div>
    );

    return (
        <div className="space-y-3">
            <div className="grid gap-2 sm:grid-cols-2">
                <LocationPicker locations={locations} value={locationId} disabled={busy}
                                onChange={(v) => setParam("loc", v)} />
                <PeriodPicker periods={canPickPeriod ? periods : counterPeriods} value={period}
                              canPick={canPickPeriod || counterPeriods.length > 1} disabled={busy}
                              onChange={(v) => setParam("period", v)} />
            </div>

            <WindowBanner win={scope?.window} />
            <ReopenedBanner reopened={scope?.reopened} locations={locations} locationId={locationId} period={period}
                            onOpen={openReopened} />

            {!data && loading ? <InlineLoader label={t("stk_loading")} /> : null}
            {!data && !loading && loadErr
                ? (loadMsg ? errorNote(loadMsg) : <OfflineBox onRetry={() => load()} busy={loading} />)
                : null}

            {data ? (
                <div data-testid={S.form} data-editable={editable ? "true" : "false"} data-edit-reason={reason}
                     data-status={status}
                     className={`rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-3 shadow-[var(--shadow-sm)] transition-opacity duration-150 ${
                         loading ? "opacity-60" : ""
                     }`}>
                    {/* status + who / when */}
                    <div className="flex flex-wrap items-center gap-2">
                        <span data-testid={C.statusChip} data-status={status}
                              className={`inline-flex h-7 items-center rounded-full px-2.5 text-[11px] font-bold ${STATUS_TONE[status] || STATUS_TONE.not_started}`}>
                            {statusLabel}
                        </span>
                        <span className="min-w-0 truncate text-sm font-bold text-[color:var(--ink)]">
                            {location ? tn(location) : ""}
                        </span>
                        {stamp ? (
                            <span className="w-full text-[11px] text-[color:var(--ink-faint)] sm:ms-auto sm:w-auto">
                                {fmt(t(stamp.key), { by: stamp.by })} <bdi dir="ltr">{fmtDateTime(stamp.at, lang)}</bdi>
                            </span>
                        ) : null}
                    </div>

                    {/* why the sheet is (or is not) editable: one sentence per edit_reason */}
                    {reasonText ? (
                        editable ? (
                            <p className="mt-2 flex items-start gap-1.5 text-[11px] font-semibold text-[color:var(--ink-muted)]">
                                <Info className="mt-px h-3.5 w-3.5 shrink-0" />{reasonText}
                            </p>
                        ) : (
                            <div className="mt-2 flex items-start gap-2 rounded-[var(--radius-sm)] bg-[color:var(--bg-surface-2)] px-3 py-2">
                                <Lock className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--ink-muted)]" />
                                <p className="text-xs font-semibold text-[color:var(--ink-muted)]">{reasonText}</p>
                            </div>
                        )
                    ) : null}
                    {unsavedLocked ? (
                        <p className="mt-2 text-[11px] font-semibold text-[color:var(--warning-soft-fg)]">
                            {fmt(t("stk_draft_locked"), { n: fmtQuantity(unsavedLocked, lang) })}
                        </p>
                    ) : null}

                    {/* search, «غير المجرود فقط», group chips */}
                    <div className="mt-3 space-y-2">
                        <div className="relative">
                            <Search className="pointer-events-none absolute start-2.5 top-3 h-4 w-4 text-[color:var(--ink-faint)]" />
                            <Input data-testid={S.search} value={q} onChange={(e) => setQ(e.target.value)}
                                   placeholder={t("stk_search")} autoComplete="off" type="search"
                                   className="h-10 w-full ps-8 text-base" />
                        </div>
                        <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1 scrollbar-thin">
                            <button type="button" data-testid={S.uncounted} aria-pressed={uncountedOnly}
                                    data-active={uncountedOnly ? "true" : "false"}
                                    onClick={() => setUncountedOnly((v) => !v)} className={chipClass(uncountedOnly)}>
                                {t("stk_uncounted_only")}
                                <bdi dir="ltr" className="ms-1 opacity-80">{fmtQuantity(missingSaved, lang)}</bdi>
                            </button>
                            {groups.length > 1 ? (
                                <>
                                    <span aria-hidden className="mx-0.5 w-px shrink-0 self-stretch bg-[color:var(--border-soft)]" />
                                    <button type="button" data-testid={S.group("all")}
                                            data-active={group === "all" ? "true" : "false"}
                                            onClick={() => setGroup("all")} className={chipClass(group === "all")}>
                                        {t("stk_all_groups")}
                                    </button>
                                    {groups.map((g, i) => (
                                        <button key={g || "-"} type="button" data-testid={S.group(i)} data-group={g}
                                                data-active={group === g ? "true" : "false"}
                                                onClick={() => setGroup(g)} className={chipClass(group === g)}>
                                            {g || t("stk_no_group")}
                                        </button>
                                    ))}
                                </>
                            ) : null}
                        </div>
                    </div>

                    {/* the grouped list; a group's header sticks to the top of the list */}
                    <div ref={listRef} className="mt-2 max-h-[60vh] overflow-y-auto scrollbar-thin">
                        {!items.length ? (
                            <p className="py-10 text-center text-sm text-[color:var(--ink-muted)]">{t("stk_no_items")}</p>
                        ) : !shown.length ? (
                            <p className="py-10 text-center text-sm text-[color:var(--ink-muted)]">
                                {uncountedOnly && !q.trim() ? t("stk_all_counted") : t("stk_no_match")}
                            </p>
                        ) : sections.map(([g, rows]) => {
                            const [done, all] = groupProgress(g);
                            return (
                                <section key={g || "-"} data-group={g}>
                                    <h3 className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] px-1 py-1.5 text-xs font-bold text-[color:var(--brand-brown)]">
                                        <span className="truncate">{g || t("stk_no_group")}</span>
                                        <bdi dir="ltr" className="shrink-0 text-[11px] font-semibold text-[color:var(--ink-faint)]">
                                            {fmtQuantity(done, lang)}/{fmtQuantity(all, lang)}
                                        </bdi>
                                    </h3>
                                    {rows.map((it) => (
                                        <CountLine key={it.id} item={it} entry={entryOf(it)} line={lines[it.id]}
                                                   dirty={dirtySet.has(it.id)} error={errors[it.id]}
                                                   value={lineValue(it)} editable={editable}
                                                   onChange={setEntry} onEnter={focusNext} />
                                    ))}
                                </section>
                            );
                        })}
                    </div>

                    {saveFailed ? <OfflineBox onRetry={save} busy={busy} text={t("stk_offline_not_saved")} /> : null}
                    {!saveFailed && loadErr && !loading
                        ? (loadMsg ? <div className="mt-3">{errorNote(loadMsg)}</div>
                            : <OfflineBox onRetry={() => load({ quiet: true })} busy={loading} />)
                        : null}

                    {/* the bar: progress, value, unsaved lines, save / submit */}
                    <div className="sticky bottom-0 mt-3 space-y-2 border-t border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] pt-2">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-semibold text-[color:var(--ink-muted)]">
                            <span data-testid={S.progress} data-counted={countedN} data-total={items.length}>
                                <bdi dir="ltr">{fmtQuantity(countedN, lang)}/{fmtQuantity(items.length, lang)}</bdi>{" "}
                                {t("stk_items_suffix")}
                            </span>
                            {totalValue !== null ? (
                                <span data-testid={S.total} data-value={totalValue} className="text-[color:var(--brand-brown)]">
                                    {t("stk_total_value")}: <bdi>{fmtMoney(totalValue, lang)}</bdi>
                                    {data?.totals?.missing_cost_n ? (
                                        <span className="ms-1 font-normal text-[color:var(--ink-faint)]">
                                            ({fmt(t("stk_missing_cost_n"), { n: fmtQuantity(data.totals.missing_cost_n, lang) })})
                                        </span>
                                    ) : null}
                                </span>
                            ) : null}
                            {dirtySet.size ? (
                                <span data-testid={S.dirty} data-count={dirtySet.size}
                                      className="flex items-center gap-1.5 text-[color:var(--brand-orange)]">
                                    <span aria-hidden className="h-2 w-2 rounded-full bg-[color:var(--brand-orange)]" />
                                    {fmt(t("stk_unsaved_n"), { n: fmtQuantity(dirtySet.size, lang) })}
                                </span>
                            ) : null}
                        </div>
                        {editable ? (
                            <div className="flex gap-2">
                                <Button data-testid={S.save} onClick={save} disabled={busy || !dirtySet.size}
                                        variant={canSubmit ? "outline" : "default"}
                                        className="h-11 flex-1 gap-2 text-sm font-bold">
                                    <Save className="h-4 w-4" />
                                    {busy ? t("stk_saving") : t("stk_save")}
                                </Button>
                                {canSubmit ? (
                                    <Button data-testid={S.submit} onClick={startSubmit}
                                            disabled={busy || !countedN}
                                            className="h-11 flex-1 gap-2 text-sm font-bold">
                                        <Send className="h-4 w-4 rtl:-scale-x-100" />
                                        {t("stk_submit")}
                                    </Button>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                </div>
            ) : null}

            {data ? (
                <SubmitDialog open={submitOpen} onOpenChange={setSubmitOpen}
                              locationId={locationId} period={period}
                              locationName={location ? tn(location) : ""}
                              itemsN={items.length} missing={missingSaved}
                              onChanged={() => load({ quiet: true })} />
            ) : null}
        </div>
    );
}
